/**
 * File: zohoChecksService.ts
 * Path: apps/admin/src/features/accounting/services/zohoChecksService.ts
 * Description: The saved month-end Compare with Zoho results
 * (accounting/{profileId}/zohoChecks/{monthEnd}): reading them, and saving
 * one through the `accounting` callable. Firestore rules refuse any write
 * from the browser, so the server decides whether a month end matches and
 * logs it.
 *
 * Firestore comes from a getter so the rules tests run these exact reads
 * against the emulator (tests/rules/accounting.rules.test.ts).
 */

import {
  ACCOUNTING_COLLECTIONS,
  ACCOUNTING_FUNCTION,
  db,
  FunctionsService,
  toZohoCheckRecord,
  ZOHO_CHECKS_COLLECTION,
  type MonthEndEventId,
  type SavedCheck,
  type ZohoCheckRecord,
} from "@idk/config";
import { collection, getDocs, limit, query, type Firestore } from "firebase/firestore";

/** What the callable accepts for a month end (apps/server/src/features/accounting/accountingValidator.ts). */
export interface SaveZohoCheckCommand {
  action: "saveZohoCheck";
  profileId: string;
  monthEnd: string;
  checks: SavedCheck[];
  events: MonthEndEventId[];
  note: string;
}

/** Ten years of month ends: far more than the parallel run needs. */
const MOST_RECORDS = 120;

export const createZohoChecksRepo = (
  getDb: () => Firestore,
  call: (command: SaveZohoCheckCommand) => Promise<unknown> = async () => {
    throw new Error("No accounting callable configured.");
  },
) => ({
  /** Every saved month end, newest first. */
  list: async (profileId: string): Promise<ZohoCheckRecord[]> => {
    const snap = await getDocs(
      query(collection(getDb(), ACCOUNTING_COLLECTIONS.profiles, profileId, ZOHO_CHECKS_COLLECTION), limit(MOST_RECORDS)),
    );
    return snap.docs
      .flatMap((d) => {
        const record = toZohoCheckRecord(d.id, d.data());
        return record ? [record] : [];
      })
      .sort((a, b) => b.monthEnd.localeCompare(a.monthEnd));
  },

  save: (command: SaveZohoCheckCommand) => call(command) as Promise<{ monthEnd: string; matching: boolean }>,
});

export const zohoChecksRepo = createZohoChecksRepo(
  () => db,
  (command) => FunctionsService.call(ACCOUNTING_FUNCTION, command),
);
